import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MessageCircle } from 'lucide-react';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { useOrgDateFormat } from '@/lib/timezone';
import { usePermissions } from '@/features/permissions';
import type { RosterView } from '@/features/flowsheets/CareRoster';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

export type AssistantCommand = { label: string; run: () => void };
type Props = {
  view: RosterView;
  onRoster: (command: { id: string; view: RosterView }) => void;
  title?: string;
  commands?: AssistantCommand[];
};
type Answer = {
  action: 'roster' | 'patient' | 'clarify' | 'command';
  command?: string;
  message: string;
  checked_at: string;
  view?: RosterView;
  counts?: Record<string, number>;
  unique?: boolean;
  patients?: { id: string; name: string; mrn: string }[];
};
export function PatientsAssistant(props: Props) {
  const { user, organization } = useAuth();
  const { can } = usePermissions();
  const org = organization?.organization_id;
  if (!org || !user || !can('use_assistant') || !can('read_patient')) return null;
  return <RosterAssistant key={`${org}:${user.id}`} {...props} org={org} canReadRoster={can('read_flowsheet')} />;
}
export function RosterAssistant({
  org,
  view,
  onRoster,
  canReadRoster,
  title = 'Patients assistant',
  commands = [],
}: Props & { org: string; canReadRoster: boolean }) {
  const navigate = useNavigate();
  const { today } = useOrgDateFormat();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  const base = `/api/v1/organizations/${org}/assistant`;
  useEffect(() => {
    const abort = new AbortController();
    clientApi
      .get(`${base}/capabilities`, { signal: abort.signal })
      .then(({ data }) => {
        if (!abort.signal.aborted) setEnabled(data.enabled);
      })
      .catch(() => {});
    return () => {
      abort.abort();
      request.current?.abort();
    };
  }, [base]);
  function openPatient(id: string) {
    setOpen(false);
    void navigate({ to: '/patients/$patientId', params: { patientId: id }, search: { assistant: true } });
  }
  async function send(question: string) {
    if (!question.trim() || request.current) return;
    const command = commands.find(
      (c) =>
        c.label.toLocaleLowerCase() ===
        question
          .trim()
          .replace(/[.!?]+$/, '')
          .toLocaleLowerCase(),
    );
    if (command) {
      command.run();
      setText('');
      setError('');
      setAnswer(null);
      setOpen(false);
      return;
    }
    const abort = new AbortController();
    request.current = abort;
    setBusy(true);
    setError('');
    setAnswer(null);
    try {
      const { data } = await clientApi.post<Answer>(
        `${base}/roster/messages`,
        {
          request_id: crypto.randomUUID(),
          text: question.trim(),
          view,
          on_date: today(),
          commands: commands.map((c) => c.label),
        },
        { signal: abort.signal },
      );
      if (abort.signal.aborted) return;
      setAnswer(data);
      setText('');
      if (data.action === 'command') {
        const selected = commands.find((c) => c.label === data.command);
        if (selected) {
          selected.run();
          setOpen(false);
        }
      }
      if (data.action === 'roster' && data.view) onRoster({ id: crypto.randomUUID(), view: data.view });
      if (data.action === 'patient' && data.unique && data.patients?.length === 1) openPatient(data.patients[0].id);
    } catch (cause) {
      if (!abort.signal.aborted) {
        const status = (cause as { response?: { status?: number } }).response?.status;
        setError(
          status === 403
            ? 'You do not have access to this assistant action.'
            : status === 429
              ? 'Daily assistant limit reached.'
              : status === 503
                ? 'Language processing is unavailable. Try a suggested action, or ask to open a chart by name or MRN.'
                : 'Unable to complete the request. Please try again.',
        );
      }
    } finally {
      if (!abort.signal.aborted) {
        request.current = null;
        setBusy(false);
      }
    }
  }
  if (!enabled) return null;
  return (
    <Sheet
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) {
          request.current?.abort();
          request.current = null;
          setBusy(false);
        }
      }}>
      <SheetTrigger asChild>
        <Button size="sm" variant="outline">
          <MessageCircle data-icon="inline-start" />
          Assistant
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>Find a chart or explore the pregnancy roster.</SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4">
          <p className="text-sm text-muted-foreground">
            Ask to sort by due date, show overdue checklists, summarize the roster, or open a patient by name or MRN.
            Opening a chart switches to its patient assistant. Each request is independent.
          </p>
          {commands.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {commands.map((command) => (
                <Button key={command.label} variant="outline" disabled={busy} onClick={() => void send(command.label)}>
                  {command.label}
                </Button>
              ))}
            </div>
          )}
          {canReadRoster && (
            <div className="flex flex-wrap gap-2">
              {['Summarize current roster', 'Sort roster by due date', 'Show pregnancies due in the next 4 weeks'].map(
                (label) => (
                  <Button key={label} variant="outline" disabled={busy} onClick={() => void send(label)}>
                    {label}
                  </Button>
                ),
              )}
            </div>
          )}
          {busy && <p role="status">Checking current records…</p>}
          {error && <p role="alert">{error}</p>}
          {answer && (
            <div className="flex flex-col gap-3" aria-live="polite">
              <p>{answer.message}</p>
              {answer.view && (
                <p className="text-sm">
                  Status: {answer.view.status} · Filter: {answer.view.filter} · Sort: {answer.view.sort.join(', ')}
                  {answer.view.search ? ` · Search: ${answer.view.search}` : ''}
                </p>
              )}
              {answer.counts && (
                <ul>
                  {Object.entries(answer.counts).map(([status, count]) => (
                    <li key={status}>
                      {status}: {count}
                    </li>
                  ))}
                </ul>
              )}
              {answer.patients?.map((patient) => (
                <Button variant="outline" key={patient.id} onClick={() => openPatient(patient.id)}>
                  {patient.name} · MRN {patient.mrn}
                </Button>
              ))}
              <p className="text-xs text-muted-foreground">Checked {new Date(answer.checked_at).toLocaleString()}</p>
            </div>
          )}
          <form
            className="mt-auto flex flex-col gap-2 pb-4"
            onSubmit={(event) => {
              event.preventDefault();
              void send(text);
            }}>
            <Textarea
              aria-label="Ask patients assistant"
              value={text}
              onChange={(event) => setText(event.target.value)}
              maxLength={2000}
              placeholder="Open the chart for…"
            />
            <Button type="submit" disabled={busy || !text.trim()}>
              Ask Assistant
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
